import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, User, ArrowRight, Search, Filter, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import AddEmployeeWizard from '@/components/employees/AddEmployeeWizard';
import axios from 'axios';
import toast from 'react-hot-toast';
import useGranularPermissions from '@/hooks/useGranularPermissions';
import { usePharmacies } from '@/context/PharmacyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

const Employees = ({ setCurrentPage }) => {
  const [showAddWizard, setShowAddWizard] = useState(false);

  // Example employee based on the mockups
  const [employees, setEmployees] = useState([]);
  const { selectedPharmacyIds, userPharmacies } = usePharmacies();

  const getActualPharmacyId = () => {
    if (!selectedPharmacyIds || selectedPharmacyIds.includes('all')) {
      return userPharmacies?.[0]?.pharmacy_id || '';
    }
    return selectedPharmacyIds[0] || '';
  };

  const fetchEmployees = async () => {
    try {
      const currentUser = JSON.parse(sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user') || '{}');
      const config = { headers: { 'X-Pharmacy-Id': getActualPharmacyId(), 'X-User-Id': currentUser.id } };
      const res = await axios.get(`${API}/employees`, config);
      
      const mapped = res.data.map(u => ({
        id: u.id,
        firstName: u.first_name || u.username,
        lastName: u.last_name || '',
        role: u.role || 'Caissier',
        status: u.is_active !== false ? 'Actif' : 'Inactif',
        hireDate: u.start_date || new Date().toLocaleDateString('fr-FR'),
        avatar: u.avatar || '',
      }));
      setEmployees(mapped);
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors du chargement des employés');
    }
  };

  useEffect(() => {
    if (selectedPharmacyIds?.length > 0) {
      fetchEmployees();
    }
  }, [selectedPharmacyIds]);

  const handleAddEmployee = async (formData) => {
    try {
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        base_salary: formData.baseSalary,
        birth_date: formData.birthDate,
        birth_place: formData.birthPlace,
        nationality: formData.nationality,
        national_id: formData.nationalId,
        id_type: formData.idType,
        id_issue_date: formData.idIssueDate,
        id_expiry_date: formData.idExpiryDate,
        start_date: formData.startDate,
        contract_type: formData.contractType,
        work_time: formData.workTime,
        status: formData.status,
        payment_frequency: formData.paymentFrequency,
        end_date: formData.endDate,
        social_security_num: formData.socialSecurityNum,
        social_security_org: formData.socialSecurityOrg,
        affiliation_status: formData.affiliationStatus,
        affiliation_date: formData.affiliationDate,
        cnas_affiliation_num: formData.cnasAffiliationNum,
        mutuelle: formData.mutuelle,
        emergency_contact_name: formData.emergencyContactName,
        emergency_contact_relation: formData.emergencyContactRelation,
        emergency_contact_phone: formData.emergencyContactPhone,
        payment_method: formData.paymentMethod,
        bank: formData.bank,
        account_number: formData.accountNumber,
        account_holder: formData.accountHolder,
        agency: formData.agency,
        rib: formData.rib,
        bic: formData.bic,
        ccp: formData.ccp,
        observations: formData.observations
      };
      
      const currentUser = JSON.parse(sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user') || '{}');
      const config = { headers: { 'X-Pharmacy-Id': getActualPharmacyId(), 'X-User-Id': currentUser.id } };
      await axios.post(`${API}/employees`, payload, config);
      toast.success('Employé ajouté avec succès');
      setShowAddWizard(false);
      fetchEmployees();
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de l\'ajout de l\'employé');
    }
  };

  const handleDeleteEmployee = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet employé ?")) return;
    
    try {
      const currentUser = JSON.parse(sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user') || '{}');
      const config = { headers: { 'X-Pharmacy-Id': getActualPharmacyId(), 'X-User-Id': currentUser.id } };
      await axios.delete(`${API}/employees/${id}`, config);
      toast.success("Employé supprimé");
      fetchEmployees();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Employés</h1>
          <p className="text-gray-500 mt-1">Gérez votre personnel et leurs fiches de paie</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <Button 
            className="w-full sm:w-auto bg-violet-600 hover:bg-violet-700 text-white"
            onClick={() => setShowAddWizard(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Ajouter un employé
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input 
            placeholder="Rechercher un employé..." 
            className="pl-10 border-gray-200"
          />
        </div>
        <Button variant="outline" className="text-gray-600 border-gray-200">
          <Filter className="mr-2 h-4 w-4" />
          Filtres
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {employees.map((emp) => (
          <Card key={emp.id} className="hover:shadow-md transition-shadow duration-200 overflow-hidden group border-gray-200">
            <CardContent className="p-0">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                      {emp.avatar ? (
                        <img src={emp.avatar} alt={`${emp.firstName} ${emp.lastName}`} className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-6 h-6 text-gray-400 m-auto mt-3" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">{emp.firstName} {emp.lastName}</h3>
                      <p className="text-gray-500 text-sm font-medium">{emp.role}</p>
                    </div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    emp.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {emp.status}
                  </span>
                </div>
                
                <div className="space-y-2 mt-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Matricule</span>
                    <span className="font-medium text-gray-900">{emp.id}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Embauché le</span>
                    <span className="font-medium text-gray-900">{emp.hireDate}</span>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-100 bg-gray-50 p-4 flex justify-between items-center">
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteEmployee(emp.id);
                  }}
                  title="Supprimer l'employé"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  className="text-violet-600 hover:text-violet-700 hover:bg-violet-50 group-hover:bg-violet-100" 
                  onClick={() => {
                    localStorage.setItem('selectedEmployeeId', emp.id);
                    window.dispatchEvent(new CustomEvent('navigate', { detail: 'employee-details' }));
                  }}
                >
                  Voir la fiche
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {showAddWizard && (
        <AddEmployeeWizard 
          open={showAddWizard} 
          onOpenChange={setShowAddWizard}
          onSubmit={handleAddEmployee}
        />
      )}
    </div>
  );
};

export default Employees;
